import chroma from 'chroma-js';

export const color = (value: string | number | chroma.Color) => chroma(value);

export const isDark = (value: string | number | chroma.Color) => {
  // YIQ equation from http://24ways.org/2010/calculating-color-contrast
  const rgb = color(value).rgb();
  const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return yiq < 128;
};

export const isLight = (value: string | number | chroma.Color) => {
  return !isDark(value);
};

export const mix = (
  color1: string | chroma.Color,
  color2: string | chroma.Color,
  ratio?: number,
) => chroma.mix(color1, color2, ratio);

/**
 * Flatten a possibly-translucent colour onto the surface behind it.
 *
 * This is the step that makes a contrast number mean anything in this app.
 * `chroma.contrast` ignores alpha completely — it reads
 * `rgba(25, 28, 30, 0.54)` as if it were opaque `#191c1e` and reports 15.54:1
 * against the light card (`elevation.level1`, `rgb(238, 245, 248)`), where the
 * pixel a user actually sees measures 3.63:1. Both figures are for the SAME
 * surface; quoting a pair measured against two different ones is the easiest
 * way to make this argument look sloppier than it is. The
 * tokens that fail AA here fail BECAUSE of their alpha, so a checker that
 * skips this reports the opposite of the truth, cleanly and confidently.
 *
 * The surface must be opaque. A translucent background has no defined
 * composite without knowing what is behind IT, and quietly treating it as
 * opaque would hand back a plausible wrong number — the exact failure this
 * function exists to prevent — so it throws instead.
 */
export const flatten = (
  foreground: string | chroma.Color,
  surface: string | chroma.Color,
) => {
  const fg = color(foreground);
  const bg = color(surface);

  if (bg.alpha() < 1) {
    throw new Error(
      `flatten() needs an opaque surface; got ${bg.css()} (alpha ${bg.alpha()}). ` +
        'Composite it onto whatever is behind it first.',
    );
  }

  const alpha = fg.alpha();
  if (alpha >= 1) {
    return fg.alpha(1);
  }

  const [fr, fg_, fb] = fg.rgb();
  const [br, bg_, bb] = bg.rgb();
  return chroma.rgb(
    fr * alpha + br * (1 - alpha),
    fg_ * alpha + bg_ * (1 - alpha),
    fb * alpha + bb * (1 - alpha),
  );
};

/**
 * WCAG 2.1 contrast ratio between a foreground and the surface behind it,
 * measured on the composited pixel (see `flatten`).
 *
 * The thresholds this feeds: 4.5:1 for normal text (1.4.3), 3:1 for large text
 * — 18.66px bold or 24px regular — and 3:1 for graphical objects and UI
 * component boundaries (1.4.11).
 */
export const contrastRatio = (
  foreground: string | chroma.Color,
  surface: string | chroma.Color,
) => chroma.contrast(flatten(foreground, surface), color(surface).alpha(1));
